/* Gorgias chat: X on the prompts' corner.
   While Gorgias's floating prompts ("Need help?" + suggested questions) are on
   screen, Gorgias's own round launcher (the X that hides the prompts) is moved
   onto the top-left corner of the prompts, like the X on the "Get 20% Off"
   teaser (Postscript), and the "Need help?" pill is lined up with that teaser.

   How: only the CSS `translate` (and a clip-path that trims the launcher
   iframe's transparent padding to its circle + shadow) is set on the two
   Gorgias iframes, through html[data-gpc-active] and CSS variables
   (assets/gorgias-prompt-close.css). Gorgias's inline styles are never
   written, so there is no style tug-of-war, and clicks stay Gorgias's own.
   As soon as the prompts are gone (X clicked, chat opened…) or anything
   doesn't look as expected, every override is removed = Gorgias default.

   A Gorgias update that changes the hooks in CONFIG makes this do nothing
   (Gorgias default). Theme setting: "Chat X on prompt corner". */
(function () {
  'use strict';

  var CONFIG = {
    // Gorgias hooks: ids and Gorgias's semantic class names only (never the
    // generated gorgias-chat-key-* classes).
    container: '#gorgias-chat-container',
    promptsFrame: 'iframe#mount-floating-engagement-widget', // "Need help?" + suggested questions
    launcherFrame: 'iframe#chat-button', // round launcher; shows the X while the prompts are open
    promptButton: 'button', // inside promptsFrame: each prompt bubble
    needHelpButton: '.gorgias-chat-floating-ask-anything-button', // inside promptsFrame (fallback: lowest bubble)
    launcherButton: 'button', // inside launcherFrame: the circle

    // Where the "Need help?" pill goes, mirroring the "Get 20% Off" teaser:
    // its left offset and the height of its centre above the screen bottom
    // (estimated from the client screenshot; the teaser can't be measured
    // live, it is a cross-origin Postscript iframe).
    pillRight: 40,
    pillCentreBottom: 50,

    edgeMin: 8, // nothing closer than this to a screen edge
    contentGap: 2, // the X keeps this far from the prompt's icon/text
    xScale: 0.6, // size of the X: 1 = Gorgias's size. The one value to change to make it smaller (e.g. 0.6).
    maxShift: 400, // larger moves = something is off → do nothing
    settleDelays: [120, 450, 1200], // re-measure after Gorgias's own show animation
    closingMaxMs: 1500, // after the X click: prompts keep their spot until Gorgias hides them, at most this long
    waitForGorgiasMs: 120000 // stop waiting for Gorgias to load after this
  };

  // html[data-gpc-prompts]: prompts moved; html[data-gpc-active]: launcher (X) moved too.
  var root = document.documentElement;
  var PROMPT_VARS = ['--gpc-prompts-x', '--gpc-prompts-y'];
  var X_VARS = ['--gpc-x-x', '--gpc-x-y', '--gpc-x-origin', '--gpc-x-clip', '--gpc-x-scale'];
  var VARS = PROMPT_VARS.concat(X_VARS);
  var container = null;
  var containerObserver = null;
  var innerObserver = null;
  var frameResize = null;
  var boundDocs = [];
  var frameScheduled = false;
  var settleTimers = [];
  var closingSince = 0;

  function px(n) { return Math.round(n * 100) / 100 + 'px'; }

  function shown(el, win) {
    if (!el || !el.isConnected) return false;
    var cs = (win || window).getComputedStyle(el);
    if (cs.display === 'none' || cs.visibility === 'hidden' || parseFloat(cs.opacity) === 0) return false;
    var r = el.getBoundingClientRect();
    return r.width > 0 && r.height > 0;
  }

  function frameParts(frame, selector, all) {
    var doc = frame.contentDocument; // same-origin (srcless) Gorgias iframes; throws/null otherwise
    if (!doc || !doc.body) return null;
    return all ? Array.prototype.slice.call(doc.querySelectorAll(selector)) : doc.querySelector(selector);
  }

  // Safe-area insets in px (0 unless the viewport uses viewport-fit=cover).
  function safeArea() {
    var probe = document.createElement('div');
    probe.style.cssText = 'position:fixed;visibility:hidden;pointer-events:none;width:env(safe-area-inset-right,0px);height:env(safe-area-inset-bottom,0px)';
    document.body.appendChild(probe);
    var out = { right: probe.offsetWidth, bottom: probe.offsetHeight };
    probe.remove();
    return out;
  }

  // Shadow reach beyond the circle, from the launcher button's box-shadow.
  function shadowReach(el, win) {
    var s = win.getComputedStyle(el).boxShadow;
    if (!s || s === 'none') return 0;
    var reach = 0;
    s.split(/,(?![^(]*\))/).forEach(function (one) {
      if (/inset/.test(one)) return;
      var n = (one.replace(/rgba?\([^)]*\)|#[0-9a-f]+|[a-z]+\([^)]*\)/gi, '').match(/-?\d*\.?\d+px/g) || []).map(parseFloat);
      var ox = n[0] || 0, oy = n[1] || 0, blur = n[2] || 0, spread = n[3] || 0;
      reach = Math.max(reach, Math.max(Math.abs(ox), Math.abs(oy)) + blur + spread);
    });
    return reach;
  }

  function hits(cx, cy, r, rect) {
    var nx = Math.max(rect.left, Math.min(cx, rect.right));
    var ny = Math.max(rect.top, Math.min(cy, rect.bottom));
    return (cx - nx) * (cx - nx) + (cy - ny) * (cy - ny) < r * r;
  }

  // The round launcher circle, or null while it isn't the X (it becomes a wide
  // "Chat with us" button once the prompts are dismissed).
  function launcherCircle(lf) {
    if (!shown(lf)) return null;
    var circle = frameParts(lf, CONFIG.launcherButton);
    if (!circle) return null;
    var cr = circle.getBoundingClientRect();
    if (cr.width < 20 || cr.width > 100 || Math.abs(cr.width - cr.height) > 2) return null;
    return circle;
  }

  // 1. Prompts on Gorgias-default geometry → where the "Need help?" pill goes
  // (mirrored side offset, same centre line as the teaser), or null.
  function measurePrompts() {
    if (!container || !container.isConnected) return null;
    var pf = container.querySelector(CONFIG.promptsFrame);
    if (!shown(pf)) return null;
    var pwin = pf.contentWindow;
    var bubbles = (frameParts(pf, CONFIG.promptButton, true) || []).filter(function (b) { return shown(b, pwin); });
    if (!bubbles.length) return null;
    var pill = frameParts(pf, CONFIG.needHelpButton);
    if (!shown(pill, pwin)) pill = bubbles.slice().sort(function (a, b) { return b.getBoundingClientRect().bottom - a.getBoundingClientRect().bottom; })[0];

    var vw = root.clientWidth, vh = window.innerHeight, safe = safeArea();
    var pfr = pf.getBoundingClientRect();
    var ox = pfr.left + pf.clientLeft, oy = pfr.top + pf.clientTop;
    function toPage(el, dx, dy) {
      var r = el.getBoundingClientRect();
      return { left: ox + r.left + dx, top: oy + r.top + dy, right: ox + r.right + dx, bottom: oy + r.bottom + dy, width: r.width, height: r.height };
    }
    var p = toPage(pill, 0, 0);
    var centreBottom = Math.max(CONFIG.pillCentreBottom, safe.bottom + p.height / 2 + 4);
    var dx = vw - Math.max(CONFIG.pillRight, CONFIG.edgeMin + safe.right) - p.right;
    var dy = vh - centreBottom - (p.top + p.bottom) / 2;
    if (!isFinite(dx) || !isFinite(dy) || Math.abs(dx) > CONFIG.maxShift || Math.abs(dy) > CONFIG.maxShift) return null;
    if (p.left + dx < CONFIG.edgeMin) return null;
    return {
      pwin: pwin, bubbles: bubbles,
      shifted: bubbles.map(function (b) { return toPage(b, dx, dy); }),
      vars: { '--gpc-prompts-x': px(dx), '--gpc-prompts-y': px(dy) }
    };
  }

  // 2. The X on the top-left corner of the top prompt (the pill itself when
  // it's alone) → launcher offsets, or null.
  function measureLauncher(prompts) {
    var lf = container.querySelector(CONFIG.launcherFrame);
    var circle = launcherCircle(lf);
    if (!circle) return null;
    var lwin = lf.contentWindow, pwin = prompts.pwin, bubbles = prompts.bubbles;
    var cr = circle.getBoundingClientRect(), lfr = lf.getBoundingClientRect();
    var shifted = prompts.shifted;
    var anchor = shifted.slice().sort(function (a, b) { return a.top - b.top; })[0];
    var anchorEl = bubbles[shifted.indexOf(anchor)];
    var acs = pwin.getComputedStyle(anchorEl);
    var content = {
      left: anchor.left + parseFloat(acs.paddingLeft) + parseFloat(acs.borderLeftWidth),
      top: anchor.top + parseFloat(acs.paddingTop) + parseFloat(acs.borderTopWidth),
      right: anchor.right, bottom: anchor.bottom
    };
    var radius = (cr.width / 2) * CONFIG.xScale;
    var reach = shadowReach(circle, lwin) * CONFIG.xScale;
    // Centre as close to the corner as possible while the circle clears the
    // prompt's icon/text and the other bubbles.
    var d = -1;
    for (var step = 0; step <= radius; step += 0.5) {
      var cx = anchor.left - step, cy = anchor.top - step;
      if (hits(cx, cy, radius + CONFIG.contentGap, content)) continue;
      if (shifted.some(function (b) { return b !== anchor && hits(cx, cy, radius, b); })) continue;
      d = step; break;
    }
    if (d < 0) return null;
    var cxT = anchor.left - d, cyT = anchor.top - d;
    if (cxT - radius - reach < CONFIG.edgeMin || cyT - radius - reach < CONFIG.edgeMin) return null;

    var cxNow = lfr.left + lf.clientLeft + cr.left + cr.width / 2;
    var cyNow = lfr.top + lf.clientTop + cr.top + cr.height / 2;
    var lx = cxT - cxNow, ly = cyT - cyNow;
    if ([lx, ly].some(function (v) { return !isFinite(v) || Math.abs(v) > CONFIG.maxShift; })) return null;

    var originX = lf.clientLeft + cr.left + cr.width / 2, originY = lf.clientTop + cr.top + cr.height / 2;
    var clipR = cr.width / 2 + shadowReach(circle, lwin); // unscaled: clip-path is applied before scale
    return {
      '--gpc-x-x': px(lx), '--gpc-x-y': px(ly),
      '--gpc-x-origin': px(originX) + ' ' + px(originY),
      '--gpc-x-clip': 'circle(' + px(clipR) + ' at ' + px(originX) + ' ' + px(originY) + ')',
      '--gpc-x-scale': String(CONFIG.xScale)
    };
  }

  function setVars(names, values) {
    names.forEach(function (v) {
      if (values) { if (root.style.getPropertyValue(v) !== values[v]) root.style.setProperty(v, values[v]); }
      else root.style.removeProperty(v);
    });
  }

  // Launcher back to Gorgias default; the prompts stay where they are.
  function launcherOff() {
    root.removeAttribute('data-gpc-active');
    setVars(X_VARS, null);
  }

  function off() {
    closingSince = 0;
    if (!root.hasAttribute('data-gpc-prompts') && !root.hasAttribute('data-gpc-active')) return;
    root.removeAttribute('data-gpc-active');
    root.removeAttribute('data-gpc-prompts');
    setVars(VARS, null);
  }

  // The X was clicked: Gorgias turns the launcher into "Chat with us" and then
  // hides the prompts. The launcher goes home at once; the prompts keep their
  // spot (no jump back to Gorgias's default spot) until they're hidden.
  function startClosing() {
    launcherOff();
    if (!closingSince) {
      closingSince = Date.now();
      setTimeout(schedule, CONFIG.closingMaxMs + 50);
    }
  }

  // Measure on Gorgias's default layout: lift our overrides, read, put the new
  // ones back. All synchronous, so nothing is painted in between.
  function update() {
    frameScheduled = false;
    try {
      bindFrames();
      var wasFull = root.hasAttribute('data-gpc-active');
      root.removeAttribute('data-gpc-active');
      root.removeAttribute('data-gpc-prompts');
      var prompts = measurePrompts();
      var launcher = prompts && measureLauncher(prompts);
      if (prompts && launcher && !closingSince) {
        setVars(PROMPT_VARS, prompts.vars);
        setVars(X_VARS, launcher);
        root.setAttribute('data-gpc-prompts', '');
        root.setAttribute('data-gpc-active', '');
      } else if (prompts && (wasFull || closingSince) && Date.now() - (closingSince || Date.now()) < CONFIG.closingMaxMs) {
        startClosing();
        setVars(PROMPT_VARS, prompts.vars);
        root.setAttribute('data-gpc-prompts', '');
      } else {
        off();
      }
    } catch (e) {
      try { off(); } catch (e2) { /* nothing more to undo */ }
    }
  }

  function schedule() {
    if (frameScheduled) return;
    frameScheduled = true;
    window.requestAnimationFrame(update);
  }

  // React before the next paint: prompts gone → Gorgias default; X no longer
  // round (just clicked) → launcher home, prompts stay. Then re-measure.
  function onGorgiasChange() {
    try {
      if (root.hasAttribute('data-gpc-prompts')) {
        var pf = container && container.querySelector(CONFIG.promptsFrame);
        if (!shown(pf)) off();
        else if (root.hasAttribute('data-gpc-active') && !launcherCircle(container.querySelector(CONFIG.launcherFrame))) startClosing();
      }
    } catch (e) {
      off();
    }
    schedule();
  }

  function settle() {
    settleTimers.forEach(clearTimeout);
    settleTimers = CONFIG.settleDelays.map(function (ms) { return setTimeout(schedule, ms); });
  }

  // Watch inside the Gorgias iframes (prompts added/removed, launcher icon
  // swapped) while they exist; dropped when they go away.
  function bindFrames() {
    var frames = container ? [container.querySelector(CONFIG.promptsFrame), container.querySelector(CONFIG.launcherFrame)].filter(Boolean) : [];
    var docs = frames.map(function (f) { try { return f.contentDocument; } catch (e) { return null; } }).filter(Boolean);
    if (docs.length === boundDocs.length && docs.every(function (d, i) { return d === boundDocs[i]; })) return;
    if (innerObserver) innerObserver.disconnect();
    if (frameResize) frameResize.disconnect();
    innerObserver = null; frameResize = null; boundDocs = docs;
    if (!frames.length) return;
    innerObserver = new MutationObserver(onGorgiasChange);
    docs.forEach(function (d) {
      innerObserver.observe(d.documentElement, { childList: true, subtree: true, attributes: true, attributeFilter: ['style', 'class', 'hidden', 'aria-hidden'] });
      d.addEventListener('animationend', schedule, true);
      d.addEventListener('transitionend', schedule, true);
    });
    if (window.ResizeObserver) {
      frameResize = new ResizeObserver(onGorgiasChange);
      frames.forEach(function (f) { frameResize.observe(f); });
    }
    frames.forEach(function (f) {
      if (!f.__gpcLoad) { f.__gpcLoad = true; f.addEventListener('load', schedule); }
    });
    settle();
  }

  function bindContainer(el) {
    container = el;
    containerObserver = new MutationObserver(onGorgiasChange);
    containerObserver.observe(container, { childList: true, subtree: true, attributes: true, attributeFilter: ['style', 'class', 'hidden'] });
    window.addEventListener('resize', schedule);
    window.addEventListener('orientationchange', schedule);
    schedule();
  }

  function init() {
    try {
      var found = document.querySelector(CONFIG.container);
      if (found) return bindContainer(found);
      // Gorgias loads late: wait for its container (direct child of <body>), not forever.
      var bodyObserver = new MutationObserver(function () {
        var el = document.querySelector(CONFIG.container);
        if (!el) return;
        bodyObserver.disconnect();
        clearTimeout(giveUp);
        bindContainer(el);
      });
      bodyObserver.observe(document.body, { childList: true });
      var giveUp = setTimeout(function () { bodyObserver.disconnect(); }, CONFIG.waitForGorgiasMs);
    } catch (e) {
      off();
    }
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
